import { useState, type ReactNode } from "react";

import { SearchableSelect } from "../shell/SearchableSelect";

/**
 * Generic step-by-step wizard shell.
 *
 * Pass it a list of steps; each step renders its own body and tells the
 * shell whether the user can advance via `canNext`. The shell handles step
 * state, the progress bar at the top, and the Back/Next/Submit buttons at
 * the bottom. The last step receives `isLast=true` so it can render a
 * Submit button instead of Next.
 *
 * The inner WizardField + WizardSection helpers give each step page a
 * consistent label/hint look without re-implementing the markup.
 */

export interface WizardStep {
  /** Short title shown in the progress bar (e.g. "Where"). */
  title: string;
  /** Whether the user is allowed to advance from this step. */
  canNext: boolean;
  /** Text shown next to the disabled Next button to explain why. */
  blockerHint?: string;
  /** The body of the step. */
  body: ReactNode;
}

export function Wizard({
  title,
  subtitle,
  steps,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  title: string;
  subtitle?: string;
  steps: WizardStep[];
  busy: boolean;
  submitLabel: string;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const [step, setStep] = useState(1);
  const total = steps.length;
  const current = steps[step - 1];
  const isLast = step === total;
  const next = () => setStep((s) => Math.min(s + 1, total));
  const back = () => setStep((s) => Math.max(1, s - 1));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{title}</h1>
        <button className="ghost" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
      {subtitle && (
        <p className="muted" style={{ margin: "0 0 18px", fontSize: 14 }}>{subtitle}</p>
      )}

      <Progress current={step} total={total} labels={steps.map((s) => s.title)} />

      <div className="card" style={{ padding: 20 }}>
        {current.body}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
          {step > 1 ? (
            <button className="ghost" onClick={back} disabled={busy}>Back</button>
          ) : (
            <span />
          )}
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            {!current.canNext && current.blockerHint && (
              <span className="muted" style={{ fontSize: 12 }}>{current.blockerHint}</span>
            )}
            {isLast ? (
              <button onClick={onSubmit} disabled={busy || !current.canNext}>
                {busy ? "Saving…" : submitLabel}
              </button>
            ) : (
              <button onClick={next} disabled={!current.canNext}>Next</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Progress({ current, total, labels }: { current: number; total: number; labels: string[] }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 8, fontSize: 12, color: "var(--muted)", marginBottom: 6 }}>
        Step {current} of {total} <span>·</span> <strong style={{ color: "var(--text)" }}>{labels[current - 1]}</strong>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {labels.map((_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              background: i < current ? "var(--accent)" : "var(--border)",
            }}
          />
        ))}
      </div>
    </div>
  );
}

export function WizardField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div style={{ marginTop: 10 }}>
      <label style={{ display: "block", marginBottom: 4 }}>{label}</label>
      {children}
      {hint && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

export function WizardSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>{title}</h2>
      {hint && <p className="muted" style={{ margin: "0 0 14px", fontSize: 13 }}>{hint}</p>}
      {children}
    </div>
  );
}

/**
 * Constituency → Ward → Branch picker. A common need across nearly every
 * capture wizard. Returns the chosen IDs via the callbacks.
 *
 * Reused by the Intel / Cases / POI / Tasks / Performance / Popularity
 * wizards so the picker behaves identically everywhere.
 */
export function ScopePicker({
  constituencies,
  wards,
  branches,
  cid, wid, bid,
  onCidChange, onWidChange, onBidChange,
  needBranch = true,
}: {
  constituencies: { id: string; name: string }[];
  wards: { id: string; name: string }[];
  branches: { id: string; name: string }[];
  cid: string;
  wid: string;
  bid: string;
  onCidChange: (id: string) => void;
  onWidChange: (id: string) => void;
  onBidChange: (id: string) => void;
  needBranch?: boolean;
}) {
  return (
    <div className="row-3" style={{ marginTop: 8 }}>
      <WizardField label="Constituency">
        <SearchableSelect
          value={cid}
          options={constituencies.map((c) => ({ value: c.id, label: c.name }))}
          onChange={onCidChange}
          required
          emptyHint="No constituencies in your scope. Add one in Hierarchy first."
        />
      </WizardField>
      <WizardField label="Ward">
        <SearchableSelect
          value={wid}
          options={wards.map((w) => ({ value: w.id, label: w.name }))}
          onChange={onWidChange}
          disabled={!cid}
          required
          emptyHint={!cid ? "Pick a constituency first." : "No wards in this constituency."}
        />
      </WizardField>
      {needBranch && (
        <WizardField label="Branch">
          <SearchableSelect
            value={bid}
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
            onChange={onBidChange}
            disabled={!wid}
            required
            emptyHint={!wid ? "Pick a ward first." : "No branches in this ward."}
          />
        </WizardField>
      )}
    </div>
  );
}
